/**
 * ADMIN PIN AUTHENTICATION SERVICE
 *
 * Specialized service for admin two-factor authentication using PIN codes
 * sent to admin emails with comprehensive security features.
 *
 * Features:
 * - PIN generation and secure storage
 * - Rate limiting to prevent PIN spam
 * - Audit logging for all admin authentication events
 * - Professional email formatting
 * - 15-minute PIN expiry with 3 attempt limit
 * - Support for multiple admin users
 */

// Service role client calls replaced with API endpoints
import { logAdminAction } from './adminAuth'
import { getSessionHeaders } from './serverSession'

export interface AdminPinResult {
 success: boolean
 error?: string
 attemptsRemaining?: number
 nextPinAllowedAt?: Date
 verificationToken?: string
 sessionId?: string
 loginToken?: string  // v7.3.316: cryptographic proof of 2FA completion
 code?: string        // v4.7.69: machine-readable error code (MFA_TOTP_REQUIRED etc.)
 totpEnabled?: boolean // v4.7.69: returned by checkMfa()
 usedBackupCode?: boolean
 backupCodesRemaining?: number | null
}

// v4.7.69: result of pre-flight MFA capability probe
export interface AdminMfaCheck {
 success: boolean
 totpEnabled: boolean
 hasDbUser: boolean
 error?: string
}

export interface AdminPinSession {
 sessionId: string
 email: string
 pinCode: string
 expiresAt: Date
 attemptsRemaining: number
 createdAt: Date
}

export class AdminPinService {
 private static readonly PIN_EXPIRY_MINUTES = 15
 private static readonly __MAX_ATTEMPTS = 3
 private static readonly __RATE_LIMIT_MINUTES = 5 // Minimum time between PIN requests

 /**
 * Generate a secure 6-digit PIN
 */
 private static __generatePin(): string {
 // SECURITY: Use cryptographically secure random number generation
 const crypto = require('crypto')
 const randomBytes = crypto.randomBytes(4)
 const num = randomBytes.readUInt32BE(0) % 900000
 return (100000 + num).toString()
 }

 /**
 * Generate a unique session ID
 */
 private static __generateSessionId(): string {
 const crypto = require('crypto')
 return `admin_pin_${Date.now()}_${crypto.randomBytes(8).toString('hex')}`
 }

 /**
 * Generate a secure 64-character verification token
 */
 private static __generateVerificationToken(): string {
 const array = new Uint8Array(32)
 crypto.getRandomValues(array)
 return Array.from(array, b => b.toString(16).padStart(2, '0')).join('')
 }

 /**
 * Check if admin can request a new PIN (rate limiting)
 * NOTE: Rate limiting is now handled server-side in the API endpoint
 * This method is kept for backward compatibility but is unused
 */
 private static async __checkRateLimit(_adminEmail: string): Promise<{ allowed: boolean; nextAllowedAt?: Date }> {
 // Rate limiting is now handled in /api/admin/generate-pin
 // This method is deprecated and always returns allowed: true
 return { allowed: true }
 }

 /**
 * Send PIN email using the admin-specific API endpoint with verification link
 */
 private static async __sendPinEmail(pin: string, adminEmail: string, sessionId: string, verificationToken: string): Promise<boolean> {
 try {
 console.log(`Sending admin PIN email with verification link to ${adminEmail}`)

 const response = await fetch('/api/send-admin-login-pin', {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({
 email: adminEmail,
 pinCode: pin,
 verificationToken: verificationToken,
 sessionId: sessionId,
 expiryMinutes: this.PIN_EXPIRY_MINUTES
 }),
 })

 const result = await response.json()

 if (result.success) {
 console.log('Admin PIN email with verification link sent successfully:', result.emailId)
 return true
 } else {
 console.error('Failed to send admin PIN email:', result.error)
 return false
 }
 } catch (error) {
 console.error('Error sending admin PIN email:', error)
 return false
 }
 }

 /**
 * Generate and send admin PIN with verification link
 * Uses server-side API endpoint to bypass RLS
 */
 static async generateAndSendPin(adminEmail: string, password?: string): Promise<AdminPinResult> {
 try {
 console.log('Generating admin PIN via API for:', adminEmail)

 const response = await fetch('/api/admin/generate-pin', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ adminEmail, action: 'generate', password })
 })

 const result = await response.json()

 if (!response.ok) {
 // Handle rate limiting
 if (response.status === 429) {
 return {
 success: false,
 error: result.error || 'Rate limited'
 }
 }
 return {
 success: false,
 error: result.error || 'Failed to generate PIN session'
 }
 }

 if (!result.success) {
 return {
 success: false,
 error: result.error || 'Failed to generate PIN session'
 }
 }

 // Log admin action (this still works client-side with anon key for logging)
 await logAdminAction(
 adminEmail,
 'admin_pin_requested',
 'admin_pin',
 result.sessionId,
 {
 target_email: adminEmail,
 has_verification_link: true
 }
 )

 console.log('Admin PIN generated and sent successfully via API')
 return {
 success: true,
 sessionId: result.sessionId
 }

 } catch (error) {
 console.error('Error generating admin PIN:', error)
 return {
 success: false,
 error: 'Failed to generate admin PIN'
 }
 }
 }

 /**
 * Verify admin PIN
 * Uses server-side API endpoint to bypass RLS
 */
 static async verifyPin(adminEmail: string, pin: string, password?: string): Promise<AdminPinResult> {
 try {
 console.log('Verifying admin PIN via API for:', adminEmail)

 const response = await fetch('/api/admin/generate-pin', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ adminEmail, action: 'verify', pin, password })
 })

 const result = await response.json()

 if (!response.ok || !result.success) {
 console.log('Invalid or expired admin PIN')

 // Log failed attempt
 await logAdminAction(
 adminEmail,
 'admin_pin_verification_failed',
 'admin_pin',
 'verification',
 {
 reason: 'invalid_or_expired_pin',
 timestamp: new Date().toISOString()
 }
 )

 return {
 success: false,
 error: result.error || 'Invalid or expired PIN'
 }
 }

 // Log successful verification
 await logAdminAction(
 adminEmail,
 'admin_pin_verified',
 'admin_pin',
 'api_verification',
 {
 verified_at: new Date().toISOString()
 }
 )

 console.log('Admin PIN verified successfully via API')
 return {
 success: true,
 loginToken: result.loginToken
 }

 } catch (error) {
 console.error('Error verifying admin PIN:', error)
 return {
 success: false,
 error: 'Failed to verify PIN'
 }
 }
 }

 /**
 * Check if the verification link was clicked (for polling from modal)
 * Uses server-side API endpoint to bypass RLS
 */
 static async checkLinkVerification(adminEmail: string): Promise<{ verified: boolean; verifiedViaLink: boolean; loginToken?: string }> {
 try {
 const response = await fetch('/api/admin/generate-pin', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ adminEmail, action: 'check_link' })
 })

 const result = await response.json()

 if (!response.ok || !result.success) {
 return { verified: false, verifiedViaLink: false }
 }

 return {
 verified: result.verified === true,
 verifiedViaLink: result.verifiedViaLink === true,
 loginToken: result.loginToken
 }
 } catch (error) {
 console.error('Error checking link verification:', error)
 return { verified: false, verifiedViaLink: false }
 }
 }

 /**
 * v4.7.69: Pre-flight probe to decide which second factor to render.
 * Returns totpEnabled=true if the admin has a working authenticator app;
 * in that case the modal must NOT call generateAndSendPin/verifyPin and
 * must instead submit codes through verifyTotp().
 */
 static async checkMfa(adminEmail: string, password?: string): Promise<AdminMfaCheck> {
  try {
   const response = await fetch('/api/admin/generate-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
    body: JSON.stringify({ adminEmail, action: 'check_mfa', password })
   })
   const result = await response.json()
   if (!response.ok || !result.success) {
    return {
     success: false,
     totpEnabled: false,
     hasDbUser: false,
     error: result.error || 'Failed to query MFA status'
    }
   }
   return {
    success: true,
    totpEnabled: !!result.totpEnabled,
    hasDbUser: !!result.hasDbUser
   }
  } catch (error) {
   console.error('Error checking admin MFA status:', error)
   return { success: false, totpEnabled: false, hasDbUser: false, error: 'Failed to query MFA status' }
  }
 }

 /**
 * v4.7.69: Verify an authenticator-app TOTP (6 digits) or a single-use backup
 * code (8 digits) and exchange it for a login token. Server-side verifyMFA()
 * enforces per-user lockout, atomic backup-code consumption, and timing-safe
 * comparison. The caller is responsible for blocking non-digit input upstream.
 */
 static async verifyTotp(adminEmail: string, code: string, password?: string): Promise<AdminPinResult> {
  try {
   const response = await fetch('/api/admin/generate-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
    body: JSON.stringify({ adminEmail, action: 'verify_totp', pin: code, password })
   })
   const result = await response.json()

   if (!response.ok || !result.success) {
    await logAdminAction(
     adminEmail,
     'admin_totp_verification_failed',
     'admin_mfa',
     'totp_verification',
     {
      reason: result.code || 'invalid_or_locked',
      timestamp: new Date().toISOString()
     }
    )
    return {
     success: false,
     error: result.error || 'Invalid authenticator code',
     code: result.code
    }
   }

   await logAdminAction(
    adminEmail,
    'admin_totp_verified',
    'admin_mfa',
    'totp_verification',
    {
     verified_at: new Date().toISOString(),
     used_backup_code: !!result.usedBackupCode
    }
   )

   return {
    success: true,
    loginToken: result.loginToken,
    usedBackupCode: !!result.usedBackupCode,
    backupCodesRemaining: result.backupCodesRemaining ?? null
   }
  } catch (error) {
   console.error('Error verifying admin TOTP:', error)
   return { success: false, error: 'Failed to verify authenticator code' }
  }
 }

 /**
 * Clean up expired PIN sessions (maintenance function)
 * Uses server-side API endpoint to bypass RLS
 */
 static async cleanupExpiredSessions(): Promise<void> {
 try {
 const response = await fetch('/api/admin/generate-pin', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ adminEmail: 'cleanup', action: 'cleanup' })
 })

 if (response.ok) {
 console.log('Cleaned up expired admin PIN sessions via API')
 }
 } catch (error) {
 console.error('Error in admin PIN cleanup:', error)
 }
 }
}
